import React, { useState } from 'react';
import ActionButton from './ActionButton';
import { useCart } from '../context/CartContext';

const formatCurrency = (value) => `\u20a6${Number(value || 0).toLocaleString()}`;

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const MinusIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M5 12h14" />
  </svg>
);

const DishCard = ({ dish, addOnOptions = [] }) => {
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedAddOns, setSelectedAddOns] = useState({});
  const [imageLoaded, setImageLoaded] = useState(false);
  const { addItem } = useCart();

  const closeAddModal = () => {
    setShowAddModal(false);
    setSelectedAddOns({});
  };

  const handleAddOnIncrement = (addOnId) => {
    setSelectedAddOns((prev) => ({
      ...prev,
      [addOnId]: (prev[addOnId] || 0) + 1,
    }));
  };

  const handleAddOnDecrement = (addOnId) => {
    setSelectedAddOns((prev) => {
      const newQty = (prev[addOnId] || 0) - 1;
      if (newQty <= 0) {
        const updated = { ...prev };
        delete updated[addOnId];
        return updated;
      }
      return { ...prev, [addOnId]: newQty };
    });
  };

  const calculateTotalPrice = () => {
    const addOnTotal = Object.entries(selectedAddOns).reduce((sum, [addOnId, qty]) => {
      const addOn = addOnOptions.find((option) => option.id === addOnId);
      return sum + (Number(addOn?.price || 0) * qty);
    }, 0);
    return Number(dish.price || 0) + addOnTotal;
  };

  const getSelectedAddOnNames = () =>
    Object.entries(selectedAddOns)
      .map(([addOnId, qty]) => {
        const addOn = addOnOptions.find((option) => option.id === addOnId);
        return addOn ? `${qty}x ${addOn.name}` : null;
      })
      .filter(Boolean);

  const handleAddToCart = () => {
    const totalPrice = calculateTotalPrice();
    const addOnNames = getSelectedAddOnNames();
    const itemName = addOnNames.length > 0
      ? `${dish.name} (+ ${addOnNames.join(', ')})`
      : dish.name;

    const addOnEntries = Object.entries(selectedAddOns)
      .filter(([, qty]) => qty > 0)
      .sort(([left], [right]) => String(left).localeCompare(String(right)));
    const addOnIds = addOnEntries.map(([id]) => id);
    const addOnSignature = addOnEntries.map(([id, qty]) => `${id}x${qty}`).join('-') || 'base';
    const addOnOptionDetails = addOnEntries.reduce((acc, [id]) => {
      const addOn = addOnOptions.find((option) => option.id === id);
      if (!addOn) return acc;
      acc[id] = {
        id,
        name: addOn.name,
        price: Number(addOn.price || 0),
      };
      return acc;
    }, {});

    addItem({
      id: `${dish.id}:${addOnSignature}`,
      productId: dish.id,
      image: dish.image || null,
      baseName: dish.name,
      basePrice: Number(dish.price || 0),
      name: itemName,
      price: totalPrice,
      type: 'dish',
      addOns: addOnIds,
      addOnNames,
      addOnQuantities: selectedAddOns,
      addOnOptionDetails,
      details: null,
    });

    closeAddModal();
  };

  return (
    <>
      <article className="dish-card">
        <div className="dish-image-container">
          <div className={`dish-image ${imageLoaded ? 'loaded' : ''}`}>
            <img
              src={dish.image}
              alt={dish.name}
              loading="lazy"
              onLoad={() => setImageLoaded(true)}
            />
          </div>
          {dish.tags && dish.tags.length > 0 && (
            <div className="dish-card-tags">
              {dish.tags.slice(0, 2).map((tag) => (
                <span key={tag} className="tag-badge">{tag}</span>
              ))}
            </div>
          )}
        </div>
        <div className="dish-content">
          <span className="dish-category">{dish.category}</span>
          <h3>{dish.name}</h3>
          <p className="dish-description">{dish.description}</p>
          <div className="dish-footer">
            <span className="dish-price">{formatCurrency(dish.price)}</span>
          </div>
          <div className="dish-actions">
            <button
              className="dish-preview-btn"
              onClick={() => setShowModal(true)}
              aria-label={`Preview ${dish.name}`}
            >
              Preview
            </button>
            <button
              className="dish-add-btn"
              onClick={() => setShowAddModal(true)}
            >
              Add to cart
            </button>
          </div>
        </div>
      </article>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="modal-close"
              onClick={() => setShowModal(false)}
              aria-label="Close modal"
            >
              x
            </button>
            <div className="modal-image">
              <img src={dish.image} alt={dish.name} />
            </div>
            <div className="modal-body">
              <span className="dish-category">{dish.category}</span>
              <h2>{dish.name}</h2>
              <p className="modal-description">{dish.description}</p>
              <div className="modal-price">
                <span className="dish-price">{formatCurrency(dish.price)}</span>
              </div>
              {dish.tags && dish.tags.length > 0 && (
                <div className="dish-tags">
                  {dish.tags.map((tag) => (
                    <span key={tag} className="tag">{tag}</span>
                  ))}
                </div>
              )}
              <div className="modal-actions">
                <ActionButton variant="outline" size="sm" onClick={() => setShowModal(false)}>
                  Close
                </ActionButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={closeAddModal}>
          <div className="modal-content add-on-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="modal-close"
              onClick={closeAddModal}
              aria-label="Close modal"
            >
              x
            </button>
            <div className="modal-body add-on-modal-body">
              <h2>Add to cart: {dish.name}</h2>
              <p className="modal-description">Select additional items to include.</p>

              <div className="add-ons-section">
                <h3>Add-ons</h3>
                <div className="add-ons-list">
                  {addOnOptions.map((addOn) => {
                    const quantity = selectedAddOns[addOn.id] || 0;
                    return (
                      <div key={addOn.id} className="add-on-item">
                        {addOn.image && (
                          <img src={addOn.image} alt={addOn.name} className="add-on-thumbnail" />
                        )}
                        <div className="add-on-details">
                          <span className="add-on-name">{addOn.name}</span>
                          <span className="add-on-price">+{formatCurrency(addOn.price)}</span>
                        </div>
                        <div className="add-on-quantity-control">
                          {quantity > 0 ? (
                            <>
                              <button
                                type="button"
                                className="addon-qty-btn addon-qty-minus"
                                onClick={() => handleAddOnDecrement(addOn.id)}
                                aria-label={`Decrease ${addOn.name} quantity`}
                              >
                                <MinusIcon />
                              </button>
                              <span className="addon-qty-display">{quantity}</span>
                              <button
                                type="button"
                                className="addon-qty-btn addon-qty-plus"
                                onClick={() => handleAddOnIncrement(addOn.id)}
                                aria-label={`Increase ${addOn.name} quantity`}
                              >
                                <PlusIcon />
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="addon-qty-btn addon-qty-add"
                              onClick={() => handleAddOnIncrement(addOn.id)}
                              aria-label={`Add ${addOn.name}`}
                            >
                              <PlusIcon />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {addOnOptions.length === 0 && (
                  <p className="modal-description">No add-ons available right now.</p>
                )}
              </div>

              <div className="modal-total">
                <div className="total-breakdown">
                  <div className="total-line">
                    <span>{dish.name}</span>
                    <span>{formatCurrency(dish.price)}</span>
                  </div>
                  {Object.entries(selectedAddOns).map(([addOnId, qty]) => {
                    const addOn = addOnOptions.find((option) => option.id === addOnId);
                    return addOn ? (
                      <div key={addOnId} className="total-line add-on-line">
                        <span>+ {qty}x {addOn.name}</span>
                        <span>{formatCurrency(Number(addOn.price || 0) * qty)}</span>
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
            </div>

            <div className="add-on-sticky-footer">
              <div>
                <span>Total</span>
                <strong>{formatCurrency(calculateTotalPrice())}</strong>
              </div>
              <ActionButton variant="primary" size="sm" onClick={handleAddToCart}>
                Add to cart
              </ActionButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DishCard;
